import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import HttpService from "../services/HttpServices";
import { toast } from "react-hot-toast";

const apiService = HttpService;

// Initial state of the slice
const initialState = {
  couponsData: [], // List of coupons
  couponDetails: null, // Details of a single coupon
  couponTypes: [], // List of coupon types
  error: null,
  loading: {
    fetchCoupons: false,
    createCoupon: false,
    fetchCouponById: false,
    updateCoupon: false,
    deleteCouponById: false,
    fetchCouponTypes: false,
    uploadCouponImage: false,
  },
};

// Async thunk for fetching coupons
export const fetchCoupons = createAsyncThunk(
  "coupon/fetchCoupons",
  async (thunkAPI) => {
    try {
      const response = await apiService.get(`/admin/coupons`);
      // console.log("RES::", response.data);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data || error.message);
    }
  }
);

// Async thunk for creating a coupon
export const createCoupon = createAsyncThunk(
  "coupon/createCoupon",
  async (couponData, { rejectWithValue }) => {
    try {
      const response = await apiService.post("/admin/coupons", couponData);
      toast.success("Coupon created successfully!");
      return response.data;
    } catch (error) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data || error.message);
    }
  }
);

// Async thunk for fetching a coupon by ID
export const fetchCouponById = createAsyncThunk(
  "coupon/fetchCouponById",
  async (couponId, { rejectWithValue }) => {
    try {
      const response = await apiService.get(`/admin/coupons/${couponId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data || error.message);
    }
  }
);

// Async thunk for editing a coupon
export const updateCoupon = createAsyncThunk(
  "coupon/updateCoupon",
  async ({ couponId, couponData }, { rejectWithValue }) => {
    try {
      const response = await apiService.put(
        `/admin/coupons/${couponId}`,
        couponData
      );
      toast.success("Coupon updated successfully!");
      return response.data;
    } catch (error) {
      toast.error("Failed to update coupon.");
      return rejectWithValue(error.response.data || error.message);
    }
  }
);

// Async thunk for deleting a coupon by ID
export const deleteCouponById = createAsyncThunk(
  "coupon/deleteCouponById",
  async (couponId, { rejectWithValue }) => {
    try {
      await apiService.delete(`/admin/coupons/${couponId}`);
      toast.success("Coupon deleted successfully!");
      return couponId;
    } catch (error) {
      toast.error("Failed to delete coupon.");
      return rejectWithValue(error.response.data || error.message);
    }
  }
);

// Async thunk for fetching coupon types
export const fetchCouponTypes = createAsyncThunk(
  "coupon/fetchCouponTypes",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.get("/admin/couponTypes");
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data || error.message);
    }
  }
);

// Async thunk for uploading a coupon image
// export const uploadCouponImage = createAsyncThunk(
//   "coupon/uploadCouponImage",
//   async (imageData, { rejectWithValue }) => {
//     try {
//       const response = await apiService.post(
//         "/admin/coupons/uploadImage",
//         imageData,
//         {
//           headers: { "Content-Type": "multipart/form-data" },
//         }
//       );
//       return response.data;
//     } catch (error) {
//       return rejectWithValue(error.response.data || error.message);
//     }
//   }
// );

// The coupon slice
const couponSlice = createSlice({
  name: "coupon",
  initialState,
  reducers: {
    resetCouponState: (state) => {
      return { ...initialState, loading: { ...initialState.loading } };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCoupons.pending, (state) => {
        state.loading.fetchCoupons = true;
      })
      .addCase(fetchCoupons.fulfilled, (state, action) => {
        state.loading.fetchCoupons = false;
        state.couponsData = action.payload.data;
      })
      .addCase(fetchCoupons.rejected, (state, action) => {
        state.loading.fetchCoupons = false;
        state.error = action.payload;
      })
      .addCase(createCoupon.pending, (state) => {
        state.loading.createCoupon = true;
      })
      .addCase(createCoupon.fulfilled, (state, action) => {
        state.loading.createCoupon = false;
        state.state.couponsData.push(action.payload);
      })
      .addCase(createCoupon.rejected, (state, action) => {
        state.loading.createCoupon = false;
        state.error = action.payload;
      })
      .addCase(fetchCouponById.pending, (state) => {
        state.loading.fetchCouponById = true;
      })
      .addCase(fetchCouponById.fulfilled, (state, action) => {
        state.loading.fetchCouponById = false;
        state.couponDetails = action.payload;
      })
      .addCase(fetchCouponById.rejected, (state, action) => {
        state.loading.fetchCouponById = false;
        state.error = action.payload;
      })
      .addCase(updateCoupon.pending, (state) => {
        state.loading.updateCoupon = true;
      })
      .addCase(updateCoupon.fulfilled, (state, action) => {
        state.loading.updateCoupon = false;
        const index = state.couponsData.findIndex(
          (coupon) => coupon.id === action.payload.id
        );
        if (index !== -1) {
          state.couponsData[index] = action.payload;
        }
      })
      .addCase(updateCoupon.rejected, (state, action) => {
        state.loading.updateCoupon = false;
        state.error = action.payload;
      })
      .addCase(deleteCouponById.pending, (state) => {
        state.loading.deleteCouponById = true;
      })
      .addCase(deleteCouponById.fulfilled, (state, action) => {
        state.loading.deleteCouponById = false;
        state.couponsData = state.couponsData.filter(
          (coupon) => coupon.id !== action.payload
        );
      })
      .addCase(deleteCouponById.rejected, (state, action) => {
        state.loading.deleteCouponById = false;
        state.error = action.payload;
      })
      .addCase(fetchCouponTypes.pending, (state) => {
        state.loading.fetchCouponTypes = true;
      })
      .addCase(fetchCouponTypes.fulfilled, (state, action) => {
        state.loading.fetchCouponTypes = false;
        state.couponTypes = action.payload;
      })
      .addCase(fetchCouponTypes.rejected, (state, action) => {
        state.loading.fetchCouponTypes = false;
        state.error = action.payload;
      })
      // .addCase(uploadCouponImage.pending, (state) => {
      //   state.loading.uploadCouponImage = true;
      // })
      // .addCase(uploadCouponImage.fulfilled, (state, action) => {
      //   state.loading.uploadCouponImage = false;
      //   // Handle the image upload response if needed
      // })
      // .addCase(uploadCouponImage.rejected, (state, action) => {
      //   state.loading.uploadCouponImage = false;
      //   state.error = action.payload;
      // });
  },
});

export const { resetCouponState } = couponSlice.actions;

export default couponSlice.reducer;